import { ShareResource } from '@hakimo-ui/hakimo/feature-shared';
import { CameraDetail } from '@hakimo-ui/hakimo/types';
import {
  useCanUpdateArmedStatus,
  useCanUpdateCamera,
} from '@hakimo-ui/hakimo/util';
import { PillTabs, TabItem } from '@hakimo-ui/shared/ui-base';
import { useMemo, useState } from 'react';
import { CameraDetailViews, CameraIntegrationType } from '../constants';
import CameraMenu from './camera-menu/CameraMenu';
import { CameraOverview } from './overview/CameraOverview';
import PlaybackVideo from './playback/PlaybackVideo';
import { Presence } from './presence/Presence';
import { CameraSettings } from './settings/CameraSettings';
import SpecialZones from './special-zones/SpecialZones';
import { viewItems } from './util';

interface Props {
  camId: string;
  camType?: string;
  camera: CameraDetail;
  onDeletedCamera: () => void;
}

export function CameraDetailsView(props: Props) {
  const { camId, camType, camera, onDeletedCamera } = props;
  const canUpdateArmedStatus = useCanUpdateArmedStatus();
  const canUpdateCamera = useCanUpdateCamera();

  const tabItems = useMemo(() => {
    const showSettings = canUpdateArmedStatus || canUpdateCamera;
    let opts: TabItem<CameraDetailViews>[] = viewItems;

    if (!showSettings) {
      opts = opts.filter((item) => item.id !== CameraDetailViews.SETTINGS);
    }
    if (camType !== CameraIntegrationType.RTSP) {
      opts = opts.filter((item) => item.id !== CameraDetailViews.PLAYBACK);
    }
    if (!camera.isPlaybackEnabled) {
      opts = opts.filter((item) => item.id !== CameraDetailViews.PLAYBACK);
    }
    return opts;
  }, [
    camType,
    camera.isPlaybackEnabled,
    canUpdateArmedStatus,
    canUpdateCamera,
  ]);

  const [activeViewId, setActiveViewId] = useState<CameraDetailViews>(
    tabItems[0].id
  );

  const [openShareLiveView, setOpenShareLiveView] = useState(false);
  const onChangeTab = (tabId: CameraDetailViews) => setActiveViewId(tabId);

  const onShare = () => {
    setOpenShareLiveView(true);
  };

  return (
    <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg min-h-screen">
      <div className="dark:border-dark-border-bg bg-onlight-bg-1 dark:bg-ondark-bg-1 sticky top-0 z-10 mb-2 flex items-center justify-between border-b border-gray-300 py-2">
        <div className="ml-6 flex flex-col justify-between gap-1">
          <h2 className="text-onlight-text-1 dark:text-ondark-text-1 pt-1 font-medium">
            {camera.name}
          </h2>
          <div className="text-onlight-text-2 dark:text-ondark-text-2 inline-flex items-center text-xs">
            {camera.location && <span>{camera.location?.name}</span>}
          </div>
        </div>
        <CameraMenu onShare={onShare} />
        {openShareLiveView && (
          <ShareResource
            resourceId={camera.id}
            resourceType="live-view"
            onClose={() => setOpenShareLiveView(false)}
          />
        )}
      </div>
      <div className="mx-auto w-[82%] py-6">
        <PillTabs
          items={tabItems}
          activeTabId={activeViewId}
          onChange={onChangeTab}
        />
        <div className="py-6">
          {activeViewId === CameraDetailViews.OVERVIEW && (
            <CameraOverview camera={camera} />
          )}
          {activeViewId === CameraDetailViews.SPECIAL_ZONES && (
            <SpecialZones camera={camera} />
          )}
          {activeViewId === CameraDetailViews.PLAYBACK && (
            <PlaybackVideo
              cameraId={camId}
              cameraTimeZone={camera.location?.timezone || 'UTC'}
            />
          )}
          {activeViewId === CameraDetailViews.PRESENCE && (
            <Presence camera={camera} />
          )}
          {activeViewId === CameraDetailViews.SETTINGS && (
            <CameraSettings camera={camera} onDeletedCamera={onDeletedCamera} />
          )}
        </div>
      </div>
    </div>
  );
}
